import type { CrowdexUIMessage } from '@crowdex/agent-contracts';

/** Recover completed server responses without losing local, undelivered follow-ups. */
export function recoverPersistedResponses(
  current: CrowdexUIMessage[],
  persisted: readonly CrowdexUIMessage[],
): CrowdexUIMessage[] {
  const completed = new Map(
    persisted
      .filter(
        (message) => message.role === 'assistant' && message.metadata?.stopReason === 'end_turn',
      )
      .map((message) => [message.id, message]),
  );
  let changed = false;
  const recovered = current.map((message) => {
    const saved = completed.get(message.id);
    if (saved === undefined || firstMessageDifference([message], [saved]) === null) return message;
    changed = true;
    return saved;
  });
  const existingIds = new Set(recovered.map((message) => message.id));
  let promptId: string | undefined;
  for (const message of persisted) {
    if (message.role === 'user') promptId = message.id;
    if (!completed.has(message.id) || existingIds.has(message.id) || promptId === undefined)
      continue;
    const promptIndex = recovered.findIndex(
      (candidate) => candidate.id === promptId && candidate.role === 'user',
    );
    if (promptIndex === -1) continue;
    // A disconnect before the first chunk leaves no assistant to replace.
    // Anchor it to the same user prompt; never import an unrelated turn or
    // insert a second answer beside an existing local one.
    const following = recovered[promptIndex + 1];
    if (following !== undefined && following.role !== 'user') continue;
    recovered.splice(promptIndex + 1, 0, message);
    existingIds.add(message.id);
    changed = true;
  }
  return changed ? recovered : current;
}

export type SyncMessage = {
  readonly id: string;
  readonly role: string;
  readonly parts: unknown;
  readonly metadata?: unknown;
};

export function firstMessageDifference(
  left: readonly SyncMessage[],
  right: readonly SyncMessage[],
): number | null {
  const sharedLength = Math.min(left.length, right.length);
  for (let index = 0; index < sharedLength; index += 1) {
    const leftMessage = left[index];
    const rightMessage = right[index];
    if (
      leftMessage === undefined ||
      rightMessage === undefined ||
      leftMessage.id !== rightMessage.id ||
      leftMessage.role !== rightMessage.role ||
      (leftMessage.parts !== rightMessage.parts &&
        JSON.stringify(leftMessage.parts) !== JSON.stringify(rightMessage.parts)) ||
      (leftMessage.metadata !== rightMessage.metadata &&
        JSON.stringify(leftMessage.metadata) !== JSON.stringify(rightMessage.metadata))
    ) {
      return index;
    }
  }
  return left.length === right.length ? null : sharedLength;
}
